/**
 * CosmosBackdrop — full-bleed animated starfield backdrop.
 *
 * Decorative only (aria-hidden="true"). "Deep Cosmo" look — no pink-violet wash.
 * Takes no props: one backdrop, used by every screen. Renders:
 *   • Base: one radial gradient indigo → midnight → void, centred slightly
 *     above the middle
 *   • Vignette: soft dark radial layer darkening the edges
 *   • .cosmos-drift wrapper containing:
 *       – Twinkle/flare background star SVG (320 stars, reduced from design's 520)
 *       – 3 hyperdrive layers (70 / 55 / 40 stars, reduced from 110 / 90 / 70)
 *       – Signature star blobs (3 hand-placed bright points) at opacity .4
 *
 * All colors via CSS custom properties — no hardcoded hex values.
 * Reduced star counts target lower-end Android TMA performance
 * (cf. ARCHITECTURE.md Risk #2).
 *
 * NOT imported from design/ — re-implemented against project tokens.
 */

import { useMemo } from 'react';
import styles from './CosmosBackdrop.module.css';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

interface Star {
  x: number;
  y: number;
  r: number;
  bright: boolean;
  flare: boolean;
  dur: number;
  dly: number;
  mn: number;
  mx: number;
}

interface HyperStar {
  x: number;
  y: number;
  r: number;
  bright: boolean;
}

interface HyperLayer {
  stars: HyperStar[];
  delay: number;
}

// Fixed design-pixel viewport of the star SVGs (they stretch to the screen).
const WIDTH = 1440;
const HEIGHT = 3200;

// --------------------------------------------------------------------------
// Seeded pseudo-random — identical inputs always yield identical output.
// Same algorithm as design/calestea/project/variant-b.jsx.
// --------------------------------------------------------------------------

function seed(n: number): number {
  const x = Math.sin(n * 9999) * 10000;
  return x - Math.floor(x);
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export function CosmosBackdrop(): JSX.Element {
  const width = WIDTH;
  const height = HEIGHT;

  // Background star field — 320 stars (vs design's 520) for TMA perf.
  const bgStars = useMemo<Star[]>(() => {
    return Array.from({ length: 320 }).map((_, i) => {
      const x     = seed(i * 2.1) * width;
      const y     = seed(i * 3.7) * height;
      const r     = seed(i * 5.3) * 1.4 + 0.2;
      const bright = seed(i * 11) > 0.86;
      const flare  = seed(i * 23) > 0.93;
      const dur    = (flare ? 12 : 4) + seed(i * 19) * (flare ? 18 : 5);
      const dly    = seed(i * 17) * (flare ? 24 : 10);
      const mn     = seed(i * 29) * 0.18 + 0.08;
      const mx     = seed(i * 31) * 0.35 + 0.55;
      return { x, y, r, bright, flare, dur, dly, mn, mx };
    });
  }, []);

  // Hyperdrive layers — 3 × offset layers at (70, 55, 40) stars.
  // Stars cluster toward the vanishing point so scale-out reads as
  // forward flight rather than a pulsing bloom.
  const hyperLayers = useMemo<HyperLayer[]>(() => {
    const counts = [70, 55, 40];
    return counts.map((count, layerIdx) => {
      const stars = Array.from({ length: count }).map((_, i) => {
        const n  = layerIdx * 1000 + i;
        const ang = seed(n * 2.13) * Math.PI * 2;
        const rad = Math.pow(seed(n * 3.71), 0.55); // bias toward center
        const cx  = width  * 0.50 + Math.cos(ang) * rad * width  * 0.65;
        const cy  = height * 0.28 + Math.sin(ang) * rad * height * 0.42;
        const r   = seed(n * 5.31) * 1.6 + 0.4 + layerIdx * 0.2;
        const bright = seed(n * 11) > 0.78;
        return { x: cx, y: cy, r, bright };
      });
      return { stars, delay: layerIdx * 12 }; // 3 × 12 s offsets in a 36 s loop
    });
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* 1. Base — one radial gradient, centred slightly above the middle */}
      <div data-cosmos-layer="base" className={styles.base} />

      {/* 2. Vignette — soft dark radial layer at the edges */}
      <div data-cosmos-layer="vignette" className={styles.vignette} />

      {/* 3. Drift wrapper — slow rotate + sway applied to all star layers */}
      <div className={styles['cosmos-drift']}>

        {/* 3a. Background twinkle / flare starfield */}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          {bgStars.map((s, i) => (
            <circle
              key={i}
              cx={s.x}
              cy={s.y}
              r={s.r}
              fill={s.bright ? 'var(--gold-bright)' : 'var(--cream)'}
              className={s.flare ? styles['star-flare'] : styles['star-twinkle']}
              style={{
                color: s.bright ? 'var(--gold-bright)' : 'var(--cream)',
                '--dur': `${s.dur}s`,
                '--dly': `${s.dly}s`,
                '--mn': s.mn,
                '--mx': s.mx,
              } as React.CSSProperties}
            />
          ))}
        </svg>

        {/* 3b. Hyperdrive layers — forward-flight star streams */}
        {hyperLayers.map((layer, layerIdx) => (
          <div
            key={layerIdx}
            data-cosmos-layer="hyperdrive"
            className={styles['hyperdrive-layer']}
            style={{ animationDelay: `${layer.delay}s` }}
          >
            <svg
              viewBox={`0 0 ${width} ${height}`}
              preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            >
              {layer.stars.map((s, j) => (
                <circle
                  key={j}
                  cx={s.x}
                  cy={s.y}
                  r={s.r}
                  fill={s.bright ? 'var(--gold-bright)' : 'var(--amber)'}
                  opacity={s.bright ? 0.95 : 0.75}
                />
              ))}
            </svg>
          </div>
        ))}

        {/* 3c. Signature stars — 3 hand-placed bright points that drift
               with the rest of the field for coherence. */}
        <div
          data-cosmos-layer="signature-stars"
          className={styles['signature-stars']}
          style={{
            background: [
              `radial-gradient(circle 4px  at 22% 12%, var(--cream), transparent 60%)`,
              `radial-gradient(circle 60px at 22% 12%, color-mix(in srgb, var(--cream) 27%, transparent), transparent 70%)`,
              `radial-gradient(circle 3px  at 78% 42%, var(--cream), transparent 60%)`,
              `radial-gradient(circle 50px at 78% 42%, color-mix(in srgb, var(--gold-bright) 33%, transparent), transparent 70%)`,
              `radial-gradient(circle 3px  at 88% 78%, var(--cream), transparent 60%)`,
              `radial-gradient(circle 40px at 88% 78%, color-mix(in srgb, var(--amber) 33%, transparent), transparent 70%)`,
            ].join(','),
          }}
        />
      </div>
    </div>
  );
}
